import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { formatDateTime } from "../lib/format";
import { History, Search, X, PartyPopper, Truck } from "lucide-react";

const ACTIONS = {
  creazione: ["Creazione", "text-emerald-700 bg-emerald-50"],
  assegnazione: ["Assegnazione", "text-sky-700 bg-sky-50"],
  riassegnazione: ["Riassegnazione", "text-indigo-700 bg-indigo-50"],
  annullamento: ["Annullamento", "text-red-700 bg-red-50"],
  cambio_stato: ["Cambio stato", "text-amber-700 bg-amber-50"],
  modifica: ["Modifica", "text-violet-700 bg-violet-50"],
  sollecito: ["Sollecito", "text-orange-700 bg-orange-50"],
  eliminazione: ["Eliminazione", "text-slate-700 bg-slate-200"],
};

export default function RequestAuditPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [operator, setOperator] = useState("all");
  const [action, setAction] = useState("all");
  const [code, setCode] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (operator !== "all") params.operator = operator;
    if (action !== "all") params.action = action;
    if (code.trim()) params.code = code.trim();
    const res = await api.get("/requests/audit", { params });
    setData(res.data);
  }, [operator, action, code]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const hasFilter = operator !== "all" || action !== "all" || code;

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto nx-fade-in pb-10">
        <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2">
          <History className="h-7 w-7 text-primary" /> Log Azioni Corse
        </h1>
        <p className="text-sm text-slate-500 mb-6">Chi ha creato, assegnato, modificato o annullato ogni trasporto ed evento.</p>

        <Card className="border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
            <Select value={operator} onValueChange={setOperator}>
              <SelectTrigger data-testid="audit-filter-operator" className="h-9 w-56"><SelectValue placeholder="Operatore" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli operatori</SelectItem>
                {(data?.operators || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="audit-filter-action" className="h-9 w-48"><SelectValue placeholder="Azione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le azioni</SelectItem>
                {Object.entries(ACTIONS).map(([k, [label]]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input data-testid="audit-filter-code" placeholder="Codice corsa..." value={code} onChange={(e) => setCode(e.target.value)} className="h-9 pl-8 w-44" />
            </div>
            {hasFilter && (
              <Button data-testid="audit-reset" size="sm" variant="ghost" className="gap-1 text-slate-500" onClick={() => { setOperator("all"); setAction("all"); setCode(""); }}>
                <X className="h-3.5 w-3.5" /> Reset
              </Button>
            )}
            <span data-testid="audit-count" className="ml-auto text-xs text-slate-400">{data ? `${data.items.length} azioni` : ""}</span>
          </div>
          {data === null ? (
            <div className="p-5 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-10 w-full" />)}</div>
          ) : data.items.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-500">Nessuna azione registrata con questi filtri.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">Data / Ora</TableHead>
                  <TableHead>Operatore</TableHead>
                  <TableHead className="w-[140px]">Azione</TableHead>
                  <TableHead className="w-[130px]">Corsa</TableHead>
                  <TableHead>Dettagli</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((a) => {
                  const [label, cls] = ACTIONS[a.action] || [a.action, "text-slate-700 bg-slate-100"];
                  return (
                    <TableRow key={a.id} data-testid="audit-row">
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">{formatDateTime(a.at)}</TableCell>
                      <TableCell className="text-sm font-medium text-slate-800">
                        {a.by_name} {a.by_role === "partner" && <span className="text-[10px] text-slate-400 font-normal">(partner)</span>}
                      </TableCell>
                      <TableCell><span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${cls}`}>{label}</span></TableCell>
                      <TableCell>
                        {a.code && (
                          <button type="button" className="font-mono text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                            disabled={a.action === "eliminazione"} onClick={() => navigate(`/operativa/richiesta/${a.request_id}`)}>
                            {a.request_type === "evento" ? <PartyPopper className="h-3 w-3" /> : <Truck className="h-3 w-3" />} {a.code}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{a.details}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
